import React, { FC, useState, useEffect, useContext } from 'react';
import { ProfileSection } from './ProfileSection';
import { sessionStore } from 'src/store/sessionStore';
import { useUpdateTrainerProfile } from 'src/api/mutations/useUpdateTrainerProfile';
import { ProfileEditContext } from '../ProfileEditContext';
import './SessionPricingSection.scss';
import { ReactComponent as CloseIcon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/profile/components/close.svg';

export const SessionPricingSection: FC = () => {
  const { currentUser } = sessionStore();
  const [isEditing, setIsEditing] = useState(false);
  const { mutateAsync: updateProfileBackend, isPending } = useUpdateTrainerProfile();

  const { globalEditAction, registerEditState } = useContext(ProfileEditContext);

  useEffect(() => {
    if (globalEditAction.count > 0) {
      if (globalEditAction.type === 'OPEN') {
        setIsEditing(true);
      } else if (globalEditAction.type === 'CLOSE') {
        setIsEditing(false);
      }
    }
  }, [globalEditAction]);

  useEffect(() => {
    registerEditState('sessionPricing', isEditing);
  }, [isEditing, registerEditState]);

  const [pricing, setPricing] = useState<{ duration: number | string; price: number | string }[]>(
    currentUser?.pricing?.length ? currentUser.pricing : [{ duration: '', price: '' }],
  );

  useEffect(() => {
    if (currentUser && !isEditing) {
      setPricing(currentUser.pricing?.length ? currentUser.pricing : [{ duration: '', price: '' }]);
    }
  }, [currentUser, isEditing]);

  const handleSave = async () => {
    if (!currentUser) return;

    const hasInvalidEntries = pricing.some(
      (p) =>
        p.duration === '' ||
        isNaN(Number(p.duration)) ||
        Number(p.duration) <= 0 ||
        Number(p.duration) > 240 ||
        p.price === '' ||
        isNaN(Number(p.price)) ||
        Number(p.price) <= 0 ||
        Number(p.price) > 10000,
    );

    if (hasInvalidEntries) {
      return;
    }

    try {
      const uid = currentUser?.uid || '';

      // Convert to numbers before saving to backend
      const cleanedPricing = pricing.map((p) => ({
        duration: Number(p.duration),
        price: Number(p.price),
      }));

      await updateProfileBackend({
        uid,
        data: { pricing: cleanedPricing },
      });
      setIsEditing(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleAddPricing = () => {
    setPricing([...pricing, { duration: '', price: '' }]);
  };

  const handleRemovePricing = (index: number) => {
    setPricing(pricing.filter((_, i) => i !== index));
  };

  const handleUpdatePricing = (index: number, field: 'duration' | 'price', value: string) => {
    setPricing(pricing.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  };

  const viewContent = (
    <div className="session-pricing-view">
      {currentUser?.pricing && currentUser.pricing.length > 0 ? (
        currentUser.pricing.map((p, i) => (
          <div key={i} className="pricing-card">
            <h3>${p.price}</h3>
            <p>{`${p.duration} minute session`}</p>
          </div>
        ))
      ) : (
        <div className="text-section-view">
          <p>{'No session pricing added yet.'}</p>
        </div>
      )}
    </div>
  );

  const editContent = (
    <div className="session-pricing-edit">
      <p className="description">{'Set your pricing for different session durations'}</p>

      <div className="pricing-input-group">
        {pricing.map((p, i) => {
          const isDurationError =
            p.duration === '' ||
            isNaN(Number(p.duration)) ||
            Number(p.duration) <= 0 ||
            Number(p.duration) > 240;
          const isPriceError =
            p.price === '' ||
            isNaN(Number(p.price)) ||
            Number(p.price) <= 0 ||
            Number(p.price) > 10000;
          const hasError = isDurationError || isPriceError;
          return (
            <div
              key={i}
              style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '16px' }}
            >
              <div className="form-group row-group" style={{ marginBottom: 0 }}>
                <input
                  type="text"
                  value={p.duration !== undefined ? p.duration : ''}
                  onChange={(e) => handleUpdatePricing(i, 'duration', e.target.value)}
                  onBlur={(e) => handleUpdatePricing(i, 'duration', e.target.value.trim())}
                  className={`duration-input ${isDurationError && p.duration !== '' ? 'error-border' : ''}`}
                  placeholder="Min"
                />
                <span className="unit">minutes - $</span>
                <input
                  type="text"
                  value={p.price !== undefined ? p.price : ''}
                  onChange={(e) => handleUpdatePricing(i, 'price', e.target.value)}
                  onBlur={(e) => handleUpdatePricing(i, 'price', e.target.value.trim())}
                  className={`price-input ${isPriceError && p.price !== '' ? 'error-border' : ''}`}
                  placeholder="Price"
                />
                <button className="remove-btn" onClick={() => handleRemovePricing(i)}>
                  {<CloseIcon />}
                </button>
              </div>
              {hasError && (p.duration !== '' || p.price !== '') && (
                <small style={{ color: '#E44E4E', marginTop: '4px' }}>
                  {isDurationError && isPriceError
                    ? 'Please enter a valid duration (1-240 mins) and price ($1-$10,000).'
                    : isDurationError
                      ? 'Please enter a valid duration between 1 and 240 minutes.'
                      : 'Please enter a valid price between $1 and $10,000.'}
                </small>
              )}
            </div>
          );
        })}
        {pricing.length === 0 && (
          <p className="no-data" style={{ marginLeft: 0 }}>
            {`Click "+ Add Option" to add pricing.`}
          </p>
        )}
      </div>
    </div>
  );

  const hasPricingError = pricing.some(
    (p) =>
      p.duration === '' ||
      isNaN(Number(p.duration)) ||
      Number(p.duration) <= 0 ||
      Number(p.duration) > 240 ||
      p.price === '' ||
      isNaN(Number(p.price)) ||
      Number(p.price) <= 0 ||
      Number(p.price) > 10000,
  );

  const isInitialEmpty = !currentUser?.pricing || currentUser.pricing.length === 0;
  const isActionDisabled = hasPricingError || (pricing.length === 0 && isInitialEmpty);

  return (
    <ProfileSection
      title="Session Pricing"
      className="align-items-center career-highlights-centificate-header"
      isEditing={isEditing}
      isSaving={isPending}
      isActionDisabled={isActionDisabled}
      onEditToggle={setIsEditing}
      onSave={handleSave}
      onCancel={() => {
        if (currentUser) {
          setPricing(
            currentUser.pricing?.length ? currentUser.pricing : [{ duration: '', price: '' }],
          );
        }
      }}
      editContent={editContent}
      headerAction={
        <button
          className="add-action-btn"
          onClick={handleAddPricing}
          disabled={pricing.length >= 5}
          style={{
            background: 'none',
            border: 'none',
            color: pricing.length >= 5 ? 'rgba(255, 255, 255, 0.4)' : '#ffb703',
            cursor: pricing.length >= 5 ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500',
          }}
        >
          {'+ Add Option'}
        </button>
      }
    >
      {viewContent}
    </ProfileSection>
  );
};
